/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';

import { toast } from 'react-toastify';

import {
  ListeQueryContext,
  ListeTable,
  ListeTableContainer,
  type ListeType,
} from '@mairistem/liste';

import type { Specialite } from '@/types';

import { request } from '@mairistem/tools';
import SpecialiteListeTableRow from './SpecialiteListeTableRow';

interface Props {
  /** Nombre de lignes */
  count?: number;
  /** Données de la liste */
  items?: Specialite[];
  /** État du chargement */
  loading?: boolean;
  /** Callback appelé lors du chargement de la liste */
  onLoad?: () => void;
  /** Callback appelé lors de la sélection d'un item */
  onSelect?: (qualification: Specialite, qualifications: Specialite[]) => void;
  /** Nombre de lignes par page */
  perPage?: number;
  /** Callback appelé lors d'un filtrage sur la liste */
  onSort?: () => void;
  /** Callback appelé lors de la sauvegarde */
  onSave?: (qualification: Specialite) => void;
  /** Spécialité sélectionnée */
  selectedSpecialite?: Specialite;
  /** Callback appelé lors de la réinitialisation des lignes */
  resetSelectedItems?: (qualification: Specialite) => void;
}

const SpecialiteListeTable = (props: Props) => {
  const {
    count = 0,
    perPage = 50,
    items = [],
    selectedSpecialite = {},
    loading = false,
    onLoad = null,
    onSelect = null,
    onSort = null,
    onSave = null,
    resetSelectedItems = null,
  } = props;

  const [errors, setErrors] = React.useState([]);

  const { addItem, removeItem, updateItem } =
    React.useContext(ListeQueryContext);

  const deleteSpecialites = React.useCallback(
    (item) => {
      request
        .delete(
          `/api/administres/recensement-citoyen/specialites/${item.identifiant}`,
        )
        .then(() => {
          removeItem(item);
        })
        .catch(() => {
          toast('Suppression impossible, spécialité liée à un recensé.', {
            type: 'error',
          });
        });
    },
    [removeItem],
  );

  const postSpecialites = React.useCallback(
    (item) => {
      request
        .post('/api/administres/recensement-citoyen/specialites', item)
        .then((i: ListeType) => {
          addItem(i);
        });
    },
    [addItem],
  );

  const putSpecialites = React.useCallback(
    (item) => {
      request
        .put(
          `/api/administres/recensement-citoyen/specialites/${item.identifiant}`,
          item,
        )
        .then((i: ListeType) => {
          updateItem(i);
        });
    },
    [updateItem],
  );

  const handleDoubleClick = React.useCallback(
    (specialite: Specialite) => {
      if (onSelect) onSelect(specialite, items);
      if (onSave) onSave(specialite);
    },
    [items, onSave, onSelect],
  );

  const handleSaveSpecialite = React.useCallback(
    async (item: Specialite, status: string) => {
      if (_.isEmpty(item?.libelle) || _.isEmpty(item?.code)) {
        setErrors(
          _.filter(
            [
              _.isEmpty(item?.libelle)
                ? {
                    field: 'libelle',
                    itemId: item?.identifiant,
                    message: 'Le libelle ne doit pas être vide',
                  }
                : null,
              _.isEmpty(item?.code)
                ? {
                    field: 'code',
                    itemId: item?.identifiant,
                    message: 'Le code ne doit pas être vide',
                  }
                : null,
            ],
            (specialite) => !_.isNil(specialite),
          ),
        );

        return false;
      }

      setErrors(null);

      if (status === 'creating') {
        try {
          await postSpecialites(item);
          return true;
        } catch {
          setErrors([
            {
              itemId: item?.identifiant,
              message: 'Les données doivent être uniques',
            },
          ]);
          return false;
        }
      } else {
        if (item?.identifiant === selectedSpecialite?.identifiant) {
          onSave(item);
          resetSelectedItems(item);
        }
        return putSpecialites(item);
      }
    },
    [
      onSave,
      selectedSpecialite,
      postSpecialites,
      putSpecialites,
      resetSelectedItems,
    ],
  );

  const handleAddSpecialite = React.useCallback(
    (newItem: Specialite) => ({
      ...newItem,
      libelle: '',
      code: '',
      ordre: 1, // On ajoute l'item en début de liste
    }),
    [],
  );

  const handleDeleteSpecialite = React.useCallback(
    (item: Specialite) => {
      if (_.isEqual(item, selectedSpecialite)) {
        onSave({});
        resetSelectedItems({});
      }

      deleteSpecialites(item);
    },
    [selectedSpecialite, deleteSpecialites, onSave, resetSelectedItems],
  );

  const handleCancelClick = React.useCallback(() => {
    setErrors([]);
  }, []);

  const renderRow = (
    specialite: Specialite,
    _invalidate: any,
    rowProps: any,
    utilities: any,
  ) => (
    <SpecialiteListeTableRow
      specialite={specialite}
      rowProps={rowProps}
      utilities={utilities}
      errors={errors}
      onDoubleClick={handleDoubleClick}
    />
  );

  return (
    <ListeTableContainer
      onAddClick={handleAddSpecialite}
      onValidateClick={handleSaveSpecialite}
      onCancelClick={handleCancelClick}
      onDeleteClick={handleDeleteSpecialite}
      additionLabel="Ajouter une nouvelle spécialité"
      items={items}
      errors={errors}
      switchToCreationIfEmpty
      allowAdditions
      allowModifications
      allowDeletions
    >
      <ListeTable
        count={count}
        loading={loading}
        onLoad={onLoad}
        onSelect={onSelect}
        perPage={perPage}
        onSort={onSort}
        selection="click"
        selectedItems={
          _.isEmpty(selectedSpecialite) ? [] : [selectedSpecialite]
        }
        showCount
        countLabel={count > 1 ? 'spécialités' : 'spécialité'}
      >
        <ListeTable.Header>
          <ListeTable.Column
            name="code"
            sortable
            width={3}
            only={['m', 'l', 'xl']}
            required
          >
            Code
          </ListeTable.Column>
          <ListeTable.Column
            name="libelle"
            sortable
            width={13}
            only={['s', 'm', 'l', 'xl']}
            required
          >
            Libellé
          </ListeTable.Column>
        </ListeTable.Header>
        {renderRow as unknown as React.ReactNode}
      </ListeTable>
    </ListeTableContainer>
  );
};

export default SpecialiteListeTable;
